import Echo from 'laravel-echo'
import emitter from 'tiny-emitter/instance'
import { storeToRefs } from 'pinia'
import { useAuthStore } from '@/stores/auth'
import Vue from 'vue'

const defer = function () {
  let res
  const promise = new Promise(function (resolve) {
    res = resolve
  })
  return {
    resolve: res,
    promise
  }
}

const subscriptions = {
  notifications: null,
  presence: null,
  channels: [],
  broadcasts: {},
  talks: []
}

let socketInfosProm = defer()
let storedUserId = null
let echoInstance = null
let isReconnecting = false
let tokenRefreshBound = false

const instanciateEcho = function (token) {
  const authStore = useAuthStore()
  const { universe } = storeToRefs(authStore)
  const universeSlug = universe.value?.slug || null
  return new Echo({
    broadcaster: 'socket.io',
    host: window.env.VUE_APP_REAL_TIME_HOST,
    client: require('socket.io-client'),
    auth: {
      headers: {
        'X-Universe-Slug': universeSlug,
        Authorization: `Bearer ${token}`
      }
    }
  })
}

const getSocketInfos = async function () {
  return socketInfosProm && socketInfosProm.promise
}

const technicalId = function (entity) {
  return `Workplace\\Core\\Api\\Broadcast\\Channels\\${entity}Channel`
}

const subscribeNotifications = function (echo, { onNotification, userId }) {
  echo.private(`notification.${userId}`).notification(onNotification)
}

const subscribeChannel = function (echo, userId, entity, onTask) {
  echo.private(`broadcast.tasks.${userId}`).on(technicalId(entity), onTask)
}

const subscribePresence = function (echo, { here, joining, leaving }) {
  echo
    .join('broadcast.presence')
    .here(here)
    .joining(joining)
    .leaving(leaving)
}

const subscribeBroadcast = function (echo, entity, id) {
  echo.private(`broadcast.${entity.description.toLowerCase()}-${id}`)
    .on(technicalId(entity.description), params => {
      emitter.emit(`${entity.description.toLowerCase()}-${id}.${params.type ?? 'status.updated'}`)
    })
}

const subscribeTalk = function (echo, id) {
  echo.private(`broadcast.channel-${id}`)
    .on(technicalId('Talk'), () => {
      emitter.emit(`channel-${id}.status.updated`)
    })
}

const replaySubscriptions = function () {
  if (!echoInstance) return

  if (subscriptions.notifications) {
    subscribeNotifications(echoInstance, subscriptions.notifications)
  }

  if (subscriptions.presence) {
    subscribePresence(echoInstance, subscriptions.presence)
  }

  subscriptions.channels.forEach(({ entity, onTask }) => {
    subscribeChannel(echoInstance, storedUserId, entity, onTask)
  })

  Object.values(subscriptions.broadcasts).forEach(({ entity, id }) => {
    subscribeBroadcast(echoInstance, entity, id)
  })

  subscriptions.talks.forEach(id => {
    subscribeTalk(echoInstance, id)
  })
}

const connect = function () {
  socketInfosProm = defer()

  return new Promise(resolve => {
    const token = Vue.$keycloak.token
    echoInstance = instanciateEcho(token)
    echoInstance.connector.socket.on('connect', () => {
      socketInfosProm.resolve({
        userId: storedUserId,
        token,
        echo: echoInstance
      })
      replaySubscriptions()
      resolve()
    })
  })
}

const bindTokenRefresh = function () {
  if (tokenRefreshBound) return
  tokenRefreshBound = true

  const keycloak = Vue.$keycloak
  const previousOnAuthRefreshSuccess = keycloak.onAuthRefreshSuccess
  const previousOnTokenExpired = keycloak.onTokenExpired

  keycloak.onAuthRefreshSuccess = function () {
    if (previousOnAuthRefreshSuccess) {
      previousOnAuthRefreshSuccess()
    }
    reconnect()
  }

  keycloak.onTokenExpired = function () {
    keycloak.updateToken(70)
      .then(refreshed => {
        if (refreshed) {
          reconnect()
        } else if (previousOnTokenExpired) {
          previousOnTokenExpired()
        }
      })
      .catch(() => {
        keycloak.logout()
      })
  }
}

const reconnect = async function () {
  if (!storedUserId || isReconnecting) return

  isReconnecting = true
  try {
    if (echoInstance) {
      echoInstance.disconnect()
      echoInstance = null
    }
    await connect()
  } finally {
    isReconnecting = false
  }
}

const clearSubscriptions = function () {
  subscriptions.notifications = null
  subscriptions.presence = null
  subscriptions.channels = []
  subscriptions.broadcasts = {}
  subscriptions.talks = []
}

export default {
  async init (userId) {
    if (!Vue.$keycloak?.token) return

    bindTokenRefresh()

    if (storedUserId !== userId && echoInstance) {
      echoInstance.disconnect()
      echoInstance = null
      socketInfosProm = defer()
      clearSubscriptions()
    }

    storedUserId = userId

    if (!echoInstance) {
      await connect()
    }

    return getSocketInfos()
  },
  async disconnect () {
    if (echoInstance) {
      echoInstance.disconnect()
      echoInstance = null
    }
    socketInfosProm = defer()
    storedUserId = null
    clearSubscriptions()
  },
  async listenNotifications (onNotification, userId) {
    subscriptions.notifications = { onNotification, userId }
    const socketInfo = await getSocketInfos()
    subscribeNotifications(socketInfo.echo, subscriptions.notifications)
  },
  async listenChannel (entity, onTask) {
    subscriptions.channels.push({ entity, onTask })
    const socketInfo = await getSocketInfos()
    subscribeChannel(socketInfo.echo, socketInfo.userId, entity, onTask)
  },
  async listenPresence (here, joining, leaving) {
    subscriptions.presence = { here, joining, leaving }
    const socketInfo = await getSocketInfos()
    subscribePresence(socketInfo.echo, subscriptions.presence)
  },
  async listenBroadcast (entity, id) {
    const key = `${entity.description.toLowerCase()}-${id}`
    subscriptions.broadcasts[key] = { entity, id }
    const socketInfo = await getSocketInfos()
    subscribeBroadcast(socketInfo.echo, entity, id)
  },
  async listenTalk (id) {
    if (!subscriptions.talks.includes(id)) {
      subscriptions.talks.push(id)
    }
    const socketInfo = await getSocketInfos()
    subscribeTalk(socketInfo.echo, id)
  },
  async leaveBroadcast (entity, id) {
    const key = `${entity.description.toLowerCase()}-${id}`
    delete subscriptions.broadcasts[key]
    const socketInfo = await getSocketInfos()
    socketInfo.echo.leave(`broadcast.${entity.description.toLowerCase()}-${id}`)
  },
  async leaveTalk (id) {
    subscriptions.talks = subscriptions.talks.filter(talkId => talkId !== id)
    const socketInfo = await getSocketInfos()
    socketInfo.echo.leave(`broadcast.channel-${id}`)
  }
}
